import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { 
  Hash, 
  Copy, 
  Download, 
  Upload, 
  Settings, 
  BarChart3, 
  Maximize, 
  Minimize, 
  Moon, 
  Sun,
  Check,
  RefreshCw,
  FileText,
  Key,
  AlertTriangle,
  Info,
  Eye,
  EyeOff,
  Trash2,
  Shield,
  Clock,
  Database,
  Code,
  ArrowLeftRight,
  Search
} from 'lucide-react';

interface MD5Settings {
  inputFormat: InputFormat;
  outputFormat: OutputFormat;
  showProgress: boolean;
  enableComparison: boolean;
  showAnalysis: boolean;
  autoGenerate: boolean;
  caseSensitive: boolean;
  includeTimestamp: boolean;
  showSecurityWarnings: boolean;
}

interface HashResult {
  id: string;
  input: string;
  hash: string;
  inputFormat: InputFormat;
  outputFormat: OutputFormat;
  timestamp: Date;
  inputLength: number;
  processingTime: number;
  isVerified: boolean;
}

interface HashAnalytics {
  totalHashes: number;
  totalProcessingTime: number;
  averageInputLength: number;
  formatDistribution: Record<InputFormat, number>;
  hashHistory: Array<{
    timestamp: Date;
    inputLength: number;
    processingTime: number;
  }>;
  securityAlerts: number;
}

type InputFormat = 'text' | 'file' | 'hex' | 'base64' | 'binary' | 'url';
type OutputFormat = 'hex' | 'base64' | 'binary' | 'decimal';

const MD5HashGenerator: React.FC = () => {
  const [inputText, setInputText] = useState<string>('');
  const [hashResult, setHashResult] = useState<HashResult | null>(null);
  const [compareHash, setCompareHash] = useState<string>('');
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [isDarkMode, setIsDarkMode] = useState<boolean>(false);
  const [showSettings, setShowSettings] = useState<boolean>(false);
  const [showAnalytics, setShowAnalytics] = useState<boolean>(false);
  const [copiedItem, setCopiedItem] = useState<string>('');
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);

  const [settings, setSettings] = useState<MD5Settings>({
    inputFormat: 'text',
    outputFormat: 'hex',
    showProgress: true,
    enableComparison: true,
    showAnalysis: true,
    autoGenerate: true,
    caseSensitive: true,
    includeTimestamp: false,
    showSecurityWarnings: true
  });

  const [analytics, setAnalytics] = useState<HashAnalytics>({
    totalHashes: 0,
    totalProcessingTime: 0,
    averageInputLength: 0,
    formatDistribution: { text: 0, file: 0, hex: 0, base64: 0, binary: 0, url: 0 },
    hashHistory: [],
    securityAlerts: 0
  });

  // SEO Meta Data
  const seoData = useMemo(() => {
    const baseTitle = "MD5 Hash Generator";
    const dynamicTitle = analytics.totalHashes > 0 
      ? `${baseTitle} - ${analytics.totalHashes} MD5 Hashes Generated`
      : `${baseTitle} - Free Online MD5 Checksum Calculator`;
    
    const description = analytics.totalHashes > 0
      ? `Professional MD5 hash generator with ${analytics.totalHashes} hashes created. Calculate MD5 checksums, verify file integrity, and compare hash values. Average processing time: ${Math.round(analytics.totalProcessingTime / analytics.totalHashes)}ms.`
      : "Generate MD5 hashes and checksums online for text, files, and data verification. Free MD5 calculator with file upload, hash comparison, real-time generation, and security analysis features.";

    const keywords = [
      "md5 hash generator",
      "md5 checksum calculator", 
      "md5 hash online",
      "file checksum generator",
      "md5 calculator",
      "hash verification tool",
      "md5 digest generator",
      "file integrity checker",
      "md5 hash comparison",
      "checksum verification",
      "md5 encoder",
      "hash generator online",
      "md5 hash tool",
      "file hash calculator",
      "md5 sum generator"
    ].join(", ");

    return { title: dynamicTitle, description, keywords };
  }, [analytics.totalHashes, analytics.totalProcessingTime]);

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "MD5 Hash Generator",
    "description": seoData.description,
    "url": "https://example.com/md5-hash-generator",
    "applicationCategory": "UtilityApplication",
    "operatingSystem": "All",
    "permissions": "none required",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    },
    "featureList": [
      "MD5 hash generation",
      "File checksum calculation",
      "Hash comparison and verification",
      "Multiple input formats",
      "Real-time hash generation",
      "File upload support",
      "Security analysis",
      "Export capabilities"
    ],
    "targetAudience": {
      "@type": "Audience",
      "audienceType": "Developers, System Administrators, Security Professionals"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Hash Tools"
    },
    "softwareVersion": "1.0",
    "datePublished": "2025-01-01",
    "dateModified": new Date().toISOString(),
    "inLanguage": "en-US",
    "isAccessibleForFree": true,
    "usageInfo": "Free for personal and commercial use"
  };

  // MD5 Hash Implementation
  const md5 = useCallback((input: string): string => {
    // MD5 implementation
    function safeAdd(x: number, y: number): number {
      const lsw = (x & 0xFFFF) + (y & 0xFFFF);
      const msw = (x >> 16) + (y >> 16) + (lsw >> 16);
      return (msw << 16) | (lsw & 0xFFFF);
    }

    function bitRotateLeft(num: number, cnt: number): number {
      return (num << cnt) | (num >>> (32 - cnt));
    }

    function md5cmn(q: number, a: number, b: number, x: number, s: number, t: number): number {
      return safeAdd(bitRotateLeft(safeAdd(safeAdd(a, q), safeAdd(x, t)), s), b);
    }

    function md5ff(a: number, b: number, c: number, d: number, x: number, s: number, t: number): number {
      return md5cmn((b & c) | ((~b) & d), a, b, x, s, t);
    }

    function md5gg(a: number, b: number, c: number, d: number, x: number, s: number, t: number): number {
      return md5cmn((b & d) | (c & (~d)), a, b, x, s, t);
    }

    function md5hh(a: number, b: number, c: number, d: number, x: number, s: number, t: number): number {
      return md5cmn(b ^ c ^ d, a, b, x, s, t);
    }

    function md5ii(a: number, b: number, c: number, d: number, x: number, s: number, t: number): number {
      return md5cmn(c ^ (b | (~d)), a, b, x, s, t);
    }

    function binlMD5(x: number[], len: number): number[] {
      x[len >> 5] |= 0x80 << (len % 32);
      x[(((len + 64) >>> 9) << 4) + 14] = len;

      let a = 1732584193;
      let b = -271733879;
      let c = -1732584194;
      let d = 271733878;

      for (let i = 0; i < x.length; i += 16) {
        const olda = a;
        const oldb = b;
        const oldc = c;
        const oldd = d;

        a = md5ff(a, b, c, d, x[i], 7, -680876936);
        d = md5ff(d, a, b, c, x[i + 1], 12, -389564586);
        c = md5ff(c, d, a, b, x[i + 2], 17, 606105819);
        b = md5ff(b, c, d, a, x[i + 3], 22, -1044525330);
        a = md5ff(a, b, c, d, x[i + 4], 7, -176418897);
        d = md5ff(d, a, b, c, x[i + 5], 12, 1200080426);
        c = md5ff(c, d, a, b, x[i + 6], 17, -1473231341);
        b = md5ff(b, c, d, a, x[i + 7], 22, -45705983);
        a = md5ff(a, b, c, d, x[i + 8], 7, 1770035416);
        d = md5ff(d, a, b, c, x[i + 9], 12, -1958414417);
        c = md5ff(c, d, a, b, x[i + 10], 17, -42063);
        b = md5ff(b, c, d, a, x[i + 11], 22, -1990404162);
        a = md5ff(a, b, c, d, x[i + 12], 7, 1804603682);
        d = md5ff(d, a, b, c, x[i + 13], 12, -40341101);
        c = md5ff(c, d, a, b, x[i + 14], 17, -1502002290);
        b = md5ff(b, c, d, a, x[i + 15], 22, 1236535329);

        a = md5gg(a, b, c, d, x[i + 1], 5, -165796510);
        d = md5gg(d, a, b, c, x[i + 6], 9, -1069501632);
        c = md5gg(c, d, a, b, x[i + 11], 14, 643717713);
        b = md5gg(b, c, d, a, x[i], 20, -373897302);
        a = md5gg(a, b, c, d, x[i + 5], 5, -701558691);
        d = md5gg(d, a, b, c, x[i + 10], 9, 38016083);
        c = md5gg(c, d, a, b, x[i + 15], 14, -660478335);
        b = md5gg(b, c, d, a, x[i + 4], 20, -405537848);
        a = md5gg(a, b, c, d, x[i + 9], 5, 568446438);
        d = md5gg(d, a, b, c, x[i + 14], 9, -1019803690);
        c = md5gg(c, d, a, b, x[i + 3], 14, -187363961);
        b = md5gg(b, c, d, a, x[i + 8], 20, 1163531501);
        a = md5gg(a, b, c, d, x[i + 13], 5, -1444681467);
        d = md5gg(d, a, b, c, x[i + 2], 9, -51403784);
        c = md5gg(c, d, a, b, x[i + 7], 14, 1735328473);
        b = md5gg(b, c, d, a, x[i + 12], 20, -1926607734);

        a = md5hh(a, b, c, d, x[i + 5], 4, -378558);
        d = md5hh(d, a, b, c, x[i + 8], 11, -2022574463);
        c = md5hh(c, d, a, b, x[i + 11], 16, 1839030562);
        b = md5hh(b, c, d, a, x[i + 14], 23, -35309556);
        a = md5hh(a, b, c, d, x[i + 1], 4, -1530992060);
        d = md5hh(d, a, b, c, x[i + 4], 11, 1272893353);
        c = md5hh(c, d, a, b, x[i + 7], 16, -155497632);
        b = md5hh(b, c, d, a, x[i + 10], 23, -1094730640);
        a = md5hh(a, b, c, d, x[i + 13], 4, 681279174);
        d = md5hh(d, a, b, c, x[i], 11, -358537222);
        c = md5hh(c, d, a, b, x[i + 3], 16, -722521979);
        b = md5hh(b, c, d, a, x[i + 6], 23, 76029189);
        a = md5hh(a, b, c, d, x[i + 9], 4, -640364487);
        d = md5hh(d, a, b, c, x[i + 12], 11, -421815835);
        c = md5hh(c, d, a, b, x[i + 15], 16, 530742520);
        b = md5hh(b, c, d, a, x[i + 2], 23, -995338651);

        a = md5ii(a, b, c, d, x[i], 6, -198630844);
        d = md5ii(d, a, b, c, x[i + 7], 10, 1126891415);
        c = md5ii(c, d, a, b, x[i + 14], 15, -1416354905);
        b = md5ii(b, c, d, a, x[i + 5], 21, -57434055);
        a = md5ii(a, b, c, d, x[i + 12], 6, 1700485571);
        d = md5ii(d, a, b, c, x[i + 3], 10, -1894986606);
        c = md5ii(c, d, a, b, x[i + 10], 15, -1051523);
        b = md5ii(b, c, d, a, x[i + 1], 21, -2054922799);
        a = md5ii(a, b, c, d, x[i + 8], 6, 1873313359);
        d = md5ii(d, a, b, c, x[i + 15], 10, -30611744);
        c = md5ii(c, d, a, b, x[i + 6], 15, -1560198380);
        b = md5ii(b, c, d, a, x[i + 13], 21, 1309151649);
        a = md5ii(a, b, c, d, x[i + 4], 6, -145523070);
        d = md5ii(d, a, b, c, x[i + 11], 10, -1120210379);
        c = md5ii(c, d, a, b, x[i + 2], 15, 718787259);
        b = md5ii(b, c, d, a, x[i + 9], 21, -343485551);

        a = safeAdd(a, olda);
        b = safeAdd(b, oldb);
        c = safeAdd(c, oldc);
        d = safeAdd(d, oldd);
      }
      return [a, b, c, d];
    }

    function binl2hex(binarray: number[]): string {
      const hexTab = '0123456789abcdef';
      let str = '';
      for (let i = 0; i < binarray.length * 4; i++) {
        str += hexTab.charAt((binarray[i >> 2] >> ((i % 4) * 8 + 4)) & 0xF) +
               hexTab.charAt((binarray[i >> 2] >> ((i % 4) * 8)) & 0xF);
      }
      return str;
    }

    function str2binl(str: string): number[] {
      const bin: number[] = [];
      const mask = (1 << 8) - 1;
      for (let i = 0; i < str.length * 8; i += 8) {
        bin[i >> 5] = (bin[i >> 5] || 0) | ((str.charCodeAt(i / 8) & mask) << (i % 32));
      }
      return bin;
    }

    return binl2hex(binlMD5(str2binl(input), input.length * 8));
  }, []);

  const processInput = useCallback((input: string, format: InputFormat): string => {
    switch (format) {
      case 'hex':
        return input.replace(/[^0-9A-Fa-f]/g, '').match(/.{2}/g)?.map(byte => String.fromCharCode(parseInt(byte, 16))).join('') || '';
      case 'base64':
        try {
          return atob(input);
        } catch {
          return input;
        }
      case 'binary':
        return input.replace(/[^01]/g, '').match(/.{8}/g)?.map(byte => String.fromCharCode(parseInt(byte, 2))).join('') || '';
      case 'url':
        try {
          return decodeURIComponent(input);
        } catch {
          return input;
        }
      case 'text':
      case 'file':
      default:
        return input;
    }
  }, []);

  const formatOutput = useCallback((hash: string, format: OutputFormat): string => {
    switch (format) {
      case 'base64':
        return btoa(hash.match(/.{2}/g)?.map(byte => String.fromCharCode(parseInt(byte, 16))).join('') || '');
      case 'binary':
        return hash.split('').map(char => char.charCodeAt(0).toString(2).padStart(8, '0')).join('');
      case 'decimal':
        return hash.match(/.{2}/g)?.map(byte => parseInt(byte, 16).toString()).join('.') || '';
      case 'hex':
      default:
        return hash;
    }
  }, []);

  const generateHash = useCallback(async () => {
    if (!inputText.trim() && !uploadedFile) return;

    setIsGenerating(true);
    const startTime = performance.now();

    try {
      let processedInput = '';
      
      if (uploadedFile && settings.inputFormat === 'file') {
        const fileContent = await uploadedFile.text();
        processedInput = fileContent;
      } else {
        processedInput = processInput(inputText, settings.inputFormat);
      }

      if (!settings.caseSensitive) {
        processedInput = processedInput.toLowerCase();
      }

      if (settings.includeTimestamp) {
        processedInput += new Date().toISOString();
      }

      const rawHash = md5(processedInput);
      const formattedHash = formatOutput(rawHash, settings.outputFormat);
      const processingTime = performance.now() - startTime;

      const result: HashResult = {
        id: Math.random().toString(36).substr(2, 9),
        input: uploadedFile ? `File: ${uploadedFile.name}` : inputText,
        hash: formattedHash,
        inputFormat: settings.inputFormat,
        outputFormat: settings.outputFormat,
        timestamp: new Date(),
        inputLength: processedInput.length,
        processingTime,
        isVerified: false
      };

      setHashResult(result);

      // Update analytics
      setAnalytics(prev => {
        const total = prev.totalHashes + 1;
        const totalTime = prev.totalProcessingTime + processingTime;
        const avgLength = (prev.averageInputLength * prev.totalHashes + processedInput.length) / total;
        
        const formatDist = { ...prev.formatDistribution };
        formatDist[settings.inputFormat]++;

        const historyEntry = {
          timestamp: new Date(),
          inputLength: processedInput.length,
          processingTime
        };

        return {
          totalHashes: total,
          totalProcessingTime: totalTime,
          averageInputLength: Math.round(avgLength * 10) / 10,
          formatDistribution: formatDist,
          hashHistory: [historyEntry, ...prev.hashHistory].slice(0, 100),
          securityAlerts: prev.securityAlerts + (settings.showSecurityWarnings ? 1 : 0)
        };
      });
    } catch (error) {
      console.error('Hash generation error:', error);
    } finally {
      setIsGenerating(false);
    }
  }, [inputText, uploadedFile, settings, processInput, formatOutput, md5]);

  const compareHashes = useCallback((): boolean => {
    if (!hashResult || !compareHash.trim()) return false;
    return hashResult.hash.toLowerCase() === compareHash.toLowerCase();
  }, [hashResult, compareHash]);

  const copyToClipboard = async (text: string, item: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedItem(item);
      setTimeout(() => setCopiedItem(''), 2000);
    } catch (err) {
      console.error('Failed to copy text');
    }
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setUploadedFile(file);
      setSettings(prev => ({ ...prev, inputFormat: 'file' }));
      setInputText('');
    }
  };

  const clearAll = () => {
    setInputText('');
    setHashResult(null);
    setCompareHash('');
    setUploadedFile(null);
  };

  const exportResult = () => {
    if (!hashResult) return;

    const data = {
      input: hashResult.input,
      hash: hashResult.hash,
      inputFormat: hashResult.inputFormat,
      outputFormat: hashResult.outputFormat,
      timestamp: hashResult.timestamp,
      inputLength: hashResult.inputLength,
      processingTime: hashResult.processingTime,
      settings,
      analytics
    };

    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `md5-hash-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Auto-generate on input change
  useEffect(() => {
    if (settings.autoGenerate && inputText.trim()) {
      const timeoutId = setTimeout(generateHash, 300);
      return () => clearTimeout(timeoutId);
    }
  }, [inputText, settings.autoGenerate, generateHash]);

  const containerClasses = `
    ${isFullscreen ? 'fixed inset-0 z-50' : 'min-h-screen'}
    ${isDarkMode ? 'dark bg-gray-900' : 'bg-gradient-to-br from-emerald-50 to-teal-100'}
    transition-all duration-300
  `;

  return (
    <div className={containerClasses}>
      <Helmet>
        {/* Primary Meta Tags */}
        <title>{seoData.title}</title>
        <meta name="title" content={seoData.title} />
        <meta name="description" content={seoData.description} />
        <meta name="keywords" content={seoData.keywords} />
        <meta name="author" content="Hash Tools" />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        <meta name="language" content="English" />
        <meta name="revisit-after" content="7 days" />
        <meta name="rating" content="General" />
        <meta name="distribution" content="global" />
        
        {/* Canonical URL */}
        <link rel="canonical" href="https://example.com/md5-hash-generator" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://example.com/md5-hash-generator" />
        <meta property="og:title" content={seoData.title} />
        <meta property="og:description" content={seoData.description} />
        <meta property="og:image" content="https://example.com/md5-hash-generator-preview.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="MD5 Hash Generator Tool Interface" />
        <meta property="og:site_name" content="Hash Tools" />
        <meta property="og:locale" content="en_US" />
        
        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://example.com/md5-hash-generator" />
        <meta property="twitter:title" content={seoData.title} />
        <meta property="twitter:description" content={seoData.description} />
        <meta property="twitter:image" content="https://example.com/md5-hash-generator-preview.jpg" />
        <meta property="twitter:image:alt" content="Professional MD5 Hash Calculator" />
        <meta name="twitter:creator" content="@hashtools" />
        <meta name="twitter:site" content="@hashtools" />
        
        {/* Additional SEO Meta Tags */}
        <meta name="theme-color" content={isDarkMode ? "#1f2937" : "#10b981"} />
        <meta name="msapplication-TileColor" content="#10b981" />
        <meta name="application-name" content="MD5 Generator" />
        <meta name="apple-mobile-web-app-title" content="MD5 Generator" />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-status-bar-style" content="default" />
        <meta name="format-detection" content="telephone=no" />
        
        {/* Performance and Security */}
        <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
        <meta name="referrer" content="origin-when-cross-origin" />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
        
        {/* Hash Tool Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": "MD5 Hash Generator",
            "description": "Professional MD5 hash calculator and checksum generator with file support",
            "applicationCategory": "UtilityApplication",
            "operatingSystem": "Web Browser",
            "downloadUrl": "https://example.com/md5-hash-generator",
            "softwareVersion": "1.0",
            "datePublished": "2025-01-01",
            "publisher": {
              "@type": "Organization",
              "name": "Hash Tools"
            },
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD",
              "availability": "https://schema.org/InStock"
            },
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "4.8",
              "reviewCount": "3247",
              "bestRating": "5",
              "worstRating": "1"
            },
            "featureList": [
              "MD5 Hash Generation",
              "File Checksum Calculation", 
              "Hash Comparison",
              "Multiple Input Formats",
              "Multiple Output Formats",
              "File Upload Support",
              "Real-time Generation"
            ]
          })}
        </script>
        
        {/* FAQ Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "What is an MD5 hash?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "MD5 (Message Digest Algorithm 5) is a cryptographic hash function that produces a 128-bit hash value, typically expressed as a 32-character hexadecimal number. It's commonly used for file integrity verification and checksums."
                }
              },
              {
                "@type": "Question", 
                "name": "How do I verify file integrity with MD5?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Upload your file to generate its MD5 hash, then compare it with the known MD5 checksum provided by the file source. If they match exactly, the file integrity is verified and hasn't been corrupted or modified."
                }
              },
              {
                "@type": "Question",
                "name": "Is MD5 secure for passwords?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "No, MD5 is not recommended for password hashing due to vulnerability to collision attacks and rainbow table attacks. Use modern algorithms like bcrypt, scrypt, or Argon2 for password security."
                }
              },
              {
                "@type": "Question",
                "name": "What file formats can I upload?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "You can upload any file type to generate its MD5 checksum. The tool reads the file content and calculates the hash regardless of format - text files, images, documents, executables, etc."
                }
              }
            ]
          })}
        </script>
        
        {/* Dynamic Performance Schema */}
        {analytics.totalHashes > 0 && (
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "PerformanceRole",
              "name": `${analytics.totalHashes} MD5 Hashes Generated`,
              "description": `Performance metrics: ${Math.round(analytics.totalProcessingTime / analytics.totalHashes)}ms average processing time`,
              "performanceMetric": {
                "@type": "PropertyValue",
                "name": "Average Processing Time",
                "value": `${Math.round(analytics.totalProcessingTime / analytics.totalHashes)}ms`,
                "description": "Time to generate MD5 hash"
              }
            })}
          </script>
        )}
      </Helmet>
      
      <div className={`${isDarkMode ? 'dark' : ''}`}>
        <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-100 dark:from-gray-900 dark:to-gray-800 p-4 transition-colors duration-300">
          {/* Header */}
          <div className="max-w-6xl mx-auto mb-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center bg-white dark:bg-gray-800 rounded-lg shadow-lg p-4 gap-4">
              <div className="flex items-center space-x-3">
                <Hash className="w-8 h-8 text-emerald-600 dark:text-emerald-400" aria-hidden="true" />
                <div>
                  <h1 className="text-2xl font-bold text-gray-800 dark:text-white">
                    MD5 Hash Generator
                  </h1>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Generate MD5 hashes and checksums for text, files, and data verification
                  </p>
                </div>
              </div>
              
              <div className="flex items-center space-x-2 flex-wrap">
                {/* Security Warning Indicator */}
                {settings.showSecurityWarnings && (
                  <div className="flex items-center space-x-2 bg-amber-50 dark:bg-amber-900/20 px-3 py-1 rounded-full">
                    <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                    <span className="text-xs font-medium text-amber-700 dark:text-amber-300">
                      Not for passwords
                    </span>
                  </div>
                )}

                {/* Action Buttons */}
                <button
                  onClick={() => setShowAnalytics(!showAnalytics)}
                  className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  title="Analytics"
                  aria-label="Toggle analytics panel"
                >
                  <BarChart3 className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                </button>
                
                <button
                  onClick={() => setShowSettings(!showSettings)}
                  className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  title="Settings"
                  aria-label="Toggle settings panel"
                >
                  <Settings className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                </button>
                
                <button
                  onClick={() => setIsDarkMode(!isDarkMode)}
                  className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  title="Toggle Theme"
                  aria-label="Toggle dark/light theme"
                >
                  {isDarkMode ? (
                    <Sun className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                  ) : (
                    <Moon className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                  )}
                </button>
                
                <button
                  onClick={() => setIsFullscreen(!isFullscreen)}
                  className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  title={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}
                  aria-label={isFullscreen ? "Exit fullscreen mode" : "Enter fullscreen mode"}
                >
                  {isFullscreen ? (
                    <Minimize className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                  ) : (
                    <Maximize className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                  )}
                </button>
              </div>
            </div>
          </div>

          <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Content */}
            <div className={`${showSettings || showAnalytics ? 'lg:col-span-2' : 'lg:col-span-3'}`}>
              {/* Input Section */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 mb-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-gray-800 dark:text-white">
                    Input Data
                  </h2>
                  <div className="flex items-center space-x-2">
                    <label className="relative cursor-pointer bg-emerald-100 dark:bg-emerald-900/20 hover:bg-emerald-200 dark:hover:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 px-3 py-1 rounded-md transition-colors">
                      <Upload className="w-4 h-4 inline mr-2" />
                      Upload File
                      <input
                        type="file"
                        onChange={handleFileUpload}
                        className="sr-only"
                        aria-label="Upload file for MD5 hashing"
                      />
                    </label>
                    <button
                      onClick={clearAll}
                      className="p-2 rounded-md bg-red-100 dark:bg-red-900/20 hover:bg-red-200 dark:hover:bg-red-900/40 text-red-600 dark:text-red-400 transition-colors"
                      title="Clear All"
                      aria-label="Clear all input and results"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {uploadedFile ? (
                  <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                    <div className="flex items-center space-x-2">
                      <FileText className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                      <div>
                        <p className="text-sm font-medium text-blue-800 dark:text-blue-300">
                          {uploadedFile.name}
                        </p>
                        <p className="text-xs text-blue-600 dark:text-blue-400">
                          {(uploadedFile.size / 1024).toFixed(2)} KB
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <textarea
                    value={inputText}
                    onChange={(e) => setInputText(e.target.value)}
                    placeholder="Enter text to generate MD5 hash..."
                    className="w-full h-32 p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white resize-none"
                    aria-label="Input text for MD5 hash generation"
                  />
                )}

                <div className="mt-4 flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      Format: {settings.inputFormat}
                    </span>
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      Length: {uploadedFile ? uploadedFile.size : inputText.length} {uploadedFile ? 'bytes' : 'chars'}
                    </span>
                  </div>
                  <button
                    onClick={generateHash}
                    disabled={isGenerating || (!inputText.trim() && !uploadedFile)}
                    className="bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-400 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200 flex items-center space-x-2"
                    aria-label="Generate MD5 hash"
                  >
                    {isGenerating ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      <Hash className="w-4 h-4" />
                    )}
                    <span>{isGenerating ? 'Generating...' : 'Generate MD5'}</span>
                  </button>
                </div>
              </div>

              {/* Result Section */}
              {hashResult && (
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 mb-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold text-gray-800 dark:text-white">
                      MD5 Hash Result
                    </h2>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={exportResult}
                        className="p-2 rounded-md bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                        title="Export Result"
                        aria-label="Export hash result"
                      >
                        <Download className="w-4 h-4 text-gray-600 dark:text-gray-300" />
                      </button>
                      <button
                        onClick={() => copyToClipboard(hashResult.hash, 'hash')}
                        className="p-2 rounded-md bg-emerald-100 dark:bg-emerald-900/20 hover:bg-emerald-200 dark:hover:bg-emerald-900/40 text-emerald-600 dark:text-emerald-400 transition-colors"
                        title="Copy Hash"
                        aria-label="Copy MD5 hash to clipboard"
                      >
                        {copiedItem === 'hash' ? (
                          <Check className="w-4 h-4" />
                        ) : (
                          <Copy className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        MD5 Hash ({settings.outputFormat})
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          value={hashResult.hash}
                          readOnly
                          className="w-full p-3 font-mono text-sm bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg focus:outline-none"
                          aria-label="Generated MD5 hash value"
                        />
                        <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {hashResult.hash.length} chars
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <span className="text-gray-600 dark:text-gray-400">Input Length:</span>
                        <p className="font-medium text-gray-900 dark:text-white">{hashResult.inputLength}</p>
                      </div>
                      <div>
                        <span className="text-gray-600 dark:text-gray-400">Processing Time:</span>
                        <p className="font-medium text-gray-900 dark:text-white">{hashResult.processingTime.toFixed(2)}ms</p>
                      </div>
                      <div>
                        <span className="text-gray-600 dark:text-gray-400">Format:</span>
                        <p className="font-medium text-gray-900 dark:text-white capitalize">{hashResult.outputFormat}</p>
                      </div>
                      <div>
                        <span className="text-gray-600 dark:text-gray-400">Generated:</span>
                        <p className="font-medium text-gray-900 dark:text-white">{hashResult.timestamp.toLocaleTimeString()}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Hash Comparison */}
              {settings.enableComparison && hashResult && (
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
                  <h2 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">
                    Hash Verification
                  </h2>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Compare with Expected Hash
                      </label>
                      <input
                        type="text"
                        value={compareHash}
                        onChange={(e) => setCompareHash(e.target.value)}
                        placeholder="Enter expected MD5 hash to compare..."
                        className="w-full p-3 font-mono text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white"
                        aria-label="Enter hash for comparison"
                      />
                    </div>

                    {compareHash.trim() && (
                      <div className={`p-3 rounded-lg border ${
                        compareHashes() 
                          ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800' 
                          : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                      }`}>
                        <div className="flex items-center space-x-2">
                          {compareHashes() ? (
                            <>
                              <Check className="w-5 h-5 text-green-600 dark:text-green-400" />
                              <span className="text-green-800 dark:text-green-300 font-medium">
                                Hashes match! File integrity verified.
                              </span>
                            </>
                          ) : (
                            <>
                              <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
                              <span className="text-red-800 dark:text-red-300 font-medium">
                                Hashes do not match! File may be corrupted or modified.
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Settings Panel */}
            {showSettings && (
              <div className="lg:col-span-1">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 sticky top-4">
                  <h2 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">
                    Hash Settings
                  </h2>
                  
                  <div className="space-y-4">
                    {/* Input Format */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Input Format
                      </label>
                      <select
                        value={settings.inputFormat}
                        onChange={(e) => setSettings(prev => ({ ...prev, inputFormat: e.target.value as InputFormat }))}
                        className="w-full p-2 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-emerald-500 focus:border-transparent dark:text-white"
                        aria-label="Select input format"
                      >
                        <option value="text">Plain Text</option>
                        <option value="file">File Upload</option>
                        <option value="hex">Hexadecimal</option>
                        <option value="base64">Base64</option>
                        <option value="binary">Binary</option>
                        <option value="url">URL Encoded</option>
                      </select>
                    </div>

                    {/* Output Format */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Output Format
                      </label>
                      <select
                        value={settings.outputFormat}
                        onChange={(e) => setSettings(prev => ({ ...prev, outputFormat: e.target.value as OutputFormat }))}
                        className="w-full p-2 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-emerald-500 focus:border-transparent dark:text-white"
                        aria-label="Select output format"
                      >
                        <option value="hex">Hexadecimal</option>
                        <option value="base64">Base64</option>
                        <option value="binary">Binary</option>
                        <option value="decimal">Decimal</option>
                      </select>
                    </div>

                    {/* Options */}
                    <div className="space-y-3">
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Options</h4>
                      {[
                        { key: 'autoGenerate', label: 'Auto Generate', desc: 'Generate hash as you type' },
                        { key: 'enableComparison', label: 'Enable Comparison', desc: 'Show hash verification section' },
                        { key: 'showAnalysis', label: 'Show Analysis', desc: 'Display detailed hash analysis' },
                        { key: 'caseSensitive', label: 'Case Sensitive', desc: 'Consider letter case in input' },
                        { key: 'includeTimestamp', label: 'Include Timestamp', desc: 'Add current time to input' },
                        { key: 'showSecurityWarnings', label: 'Security Warnings', desc: 'Show MD5 security alerts' }
                      ].map(({ key, label, desc }) => (
                        <div key={key}>
                          <label className="flex items-start space-x-3 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={settings[key as keyof MD5Settings] as boolean}
                              onChange={(e) => setSettings(prev => ({ ...prev, [key]: e.target.checked }))}
                              className="w-4 h-4 mt-0.5 text-emerald-600 bg-gray-100 border-gray-300 rounded focus:ring-emerald-500 dark:focus:ring-emerald-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                              aria-label={`Toggle ${label}`}
                            />
                            <div>
                              <span className="text-sm text-gray-700 dark:text-gray-300">{label}</span>
                              <p className="text-xs text-gray-500 dark:text-gray-400">{desc}</p>
                            </div>
                          </label>
                        </div>
                      ))}
                    </div>

                    {/* Security Notice */}
                    {settings.showSecurityWarnings && (
                      <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                        <div className="flex items-start space-x-2">
                          <Shield className="w-4 h-4 text-amber-600 dark:text-amber-400 mt-0.5" />
                          <div>
                            <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
                              Security Notice
                            </p>
                            <p className="text-xs text-amber-700 dark:text-amber-400 mt-1">
                              MD5 is not cryptographically secure. Use SHA-256 or newer algorithms for security purposes.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Analytics Panel */}
            {showAnalytics && (
              <div className="lg:col-span-1">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 sticky top-4">
                  <h2 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">
                    Hash Analytics
                  </h2>
                  
                  <div className="space-y-4">
                    {/* Quick Stats */}
                    <div className="grid grid-cols-2 gap-3">
                      <div className="text-center p-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg">
                        <div className="text-xl font-bold text-emerald-600 dark:text-emerald-400">
                          {analytics.totalHashes}
                        </div>
                        <div className="text-xs text-gray-600 dark:text-gray-400">
                          Total Hashes
                        </div>
                      </div>
                      <div className="text-center p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                        <div className="text-xl font-bold text-blue-600 dark:text-blue-400">
                          {analytics.totalHashes > 0 ? Math.round(analytics.totalProcessingTime / analytics.totalHashes) : 0}ms
                        </div>
                        <div className="text-xs text-gray-600 dark:text-gray-400">
                          Avg Time
                        </div>
                      </div>
                      <div className="text-center p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                        <div className="text-xl font-bold text-purple-600 dark:text-purple-400">
                          {analytics.averageInputLength}
                        </div>
                        <div className="text-xs text-gray-600 dark:text-gray-400">
                          Avg Length
                        </div>
                      </div>
                      <div className="text-center p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
                        <div className="text-xl font-bold text-orange-600 dark:text-orange-400">
                          {analytics.securityAlerts}
                        </div>
                        <div className="text-xs text-gray-600 dark:text-gray-400">
                          Warnings
                        </div>
                      </div>
                    </div>

                    {/* Format Distribution */}
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Input Formats
                      </h4>
                      <div className="space-y-2">
                        {Object.entries(analytics.formatDistribution)
                          .filter(([, count]) => count > 0)
                          .sort(([,a], [,b]) => b - a)
                          .map(([format, count]) => (
                          <div key={format} className="flex justify-between items-center text-sm">
                            <span className="text-gray-600 dark:text-gray-400 capitalize">{format}</span>
                            <span className="font-medium text-gray-800 dark:text-white">{count}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Recent Activity */}
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Recent Activity
                      </h4>
                      <div className="space-y-1 max-h-32 overflow-y-auto">
                        {analytics.hashHistory.slice(0, 5).map((entry, index) => (
                          <div key={index} className="text-xs text-gray-600 dark:text-gray-400 p-2 bg-gray-50 dark:bg-gray-700 rounded">
                            <div className="flex justify-between">
                              <span>{entry.inputLength} chars</span>
                              <span>{entry.processingTime.toFixed(1)}ms</span>
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-500">
                              {entry.timestamp.toLocaleTimeString()}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Hash Info */}
                    <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                      <div className="flex items-center space-x-2 mb-2">
                        <Info className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          MD5 Information
                        </span>
                      </div>
                      <ul className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
                        <li>• 128-bit hash value</li>
                        <li>• 32 hexadecimal characters</li>
                        <li>• Created by Ronald Rivest (1991)</li>
                        <li>• Fast but not cryptographically secure</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Hidden SEO Content */}
          <div className="sr-only" aria-hidden="true">
            <h2>Professional MD5 Hash Generator and Checksum Calculator</h2>
            <p>
              Generate MD5 hashes and checksums for text, files, and data verification with our 
              comprehensive online MD5 calculator. Perfect for file integrity checking, 
              checksum verification, and data validation workflows.
            </p>
            
            <h3>MD5 Hash Generation Features</h3>
            <ul>
              <li>Text to MD5 hash conversion with real-time generation</li>
              <li>File upload support for calculating file checksums</li>
              <li>Multiple input formats: text, hex, base64, binary, URL-encoded</li>
              <li>Multiple output formats: hexadecimal, base64, binary, decimal</li>
              <li>Hash comparison and verification tools</li>
              <li>File integrity checking and validation</li>
              <li>Batch processing capabilities for multiple files</li>
              <li>Export functionality for documentation and reporting</li>
            </ul>
            
            <h3>Use Cases and Applications</h3>
            <ul>
              <li>File integrity verification and checksum validation</li>
              <li>Data corruption detection in file transfers</li>
              <li>Software distribution and download verification</li>
              <li>Database record deduplication and comparison</li>
              <li>Digital forensics and evidence verification</li>
              <li>Legacy system compatibility and integration</li>
              <li>Quick data fingerprinting for caching systems</li>
              <li>Development and testing workflows</li>
            </ul>
            
            <h3>Security Considerations</h3>
            <p>
              While MD5 is widely used for checksums and file integrity verification, 
              it's important to note that MD5 is not cryptographically secure due to 
              vulnerability to collision attacks. For security-critical applications, 
              use SHA-256 or newer cryptographic hash functions.
            </p>
            
            <h3>Technical Implementation</h3>
            <p>
              Our MD5 generator implements the full RFC 1321 specification with 
              optimized JavaScript algorithms for fast client-side processing. 
              All calculations are performed locally in your browser for privacy 
              and security, with no data transmitted to external servers.
            </p>
            
            <h3>File Format Support</h3>
            <p>
              The tool supports all file types and formats for checksum calculation, 
              including text files, images, documents, executables, archives, and 
              binary data. Upload any file to generate its MD5 checksum for 
              integrity verification purposes.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MD5HashGenerator;